import React, { useState, useEffect } from 'react';
import { Modal, Table, Spinner, Alert, Button, Pagination } from 'react-bootstrap';
import { api } from '../services/api';
import { formatDateTimeRu } from '../utils/dateFormat';

const ACTION_TYPE_LABELS: Record<string, string> = {
  status_change: 'Изменение статуса',
  tz_confirm: 'Подготовка ТЗ',
  new_product_confirm: 'Подтверждение нового товара',
  stop_list_add: 'Добавление в стоп-лист',
  stop_list_remove: 'Удаление из стоп-листа',
  field_update: 'Изменение поля',
  description_update: 'Изменение описания',
  season_update: 'Изменение сезона',
  image_upload: 'Загрузка изображения',
  package_info_update: 'Обновление упаковки'
};

function formatActionType(actionType: string, fieldName?: string | null): string {
  const label = ACTION_TYPE_LABELS[actionType] || actionType;
  if (actionType === 'field_update' && fieldName) {
    return `${label} (${fieldName})`;
  }
  return label;
}

function formatValue(val: any): string {
  if (val == null) return '—';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

interface ProductHistoryModalProps {
  show: boolean;
  onHide: () => void;
  productId: string;
  productLabel?: string;
}

const ProductHistoryModal: React.FC<ProductHistoryModalProps> = ({ show, onHide, productId, productLabel }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const limit = 20;

  useEffect(() => {
    if (!show || !productId) return;
    setLoading(true);
    setError(null);
    api.getProductAuditLog(productId, page, limit)
      .then((res) => {
        setData(res.data);
        setTotal(res.total);
        setTotalPages(res.totalPages);
      })
      .catch((e: any) => {
        setError(e?.response?.data?.error || e?.message || 'Ошибка загрузки');
        setData([]);
      })
      .finally(() => setLoading(false));
  }, [show, productId, page]);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          История изменений {productLabel ? `— ${productLabel}` : `— товар ${productId}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {loading && (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        )}
        {!loading && data.length === 0 && !error && (
          <p className="text-muted mb-0">Нет записей.</p>
        )}
        {!loading && data.length > 0 && (
          <>
            <Table responsive size="sm" variant="dark">
              <thead>
                <tr>
                  <th>Дата и время</th>
                  <th>Действие</th>
                  <th>Было</th>
                  <th>Стало</th>
                  <th>Пользователь</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr key={row.id}>
                    <td>{formatDateTimeRu(row.created_at)}</td>
                    <td>{formatActionType(row.action_type, row.field_name)}</td>
                    <td className="text-break" style={{ maxWidth: 120 }}>{formatValue(row.old_value)}</td>
                    <td className="text-break" style={{ maxWidth: 120 }}>{formatValue(row.new_value)}</td>
                    <td>{row.user_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-2">
                <span className="small text-muted">Всего: {total}</span>
                <Pagination size="sm" className="mb-0">
                  <Pagination.Prev disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />
                  <Pagination.Item active>{page} / {totalPages}</Pagination.Item>
                  <Pagination.Next disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} />
                </Pagination>
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Закрыть</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ProductHistoryModal;
